import type { WebPushConfig } from './web-push-config';

interface EmarsysSdk {
  init: (options: {
    applicationCode: string;
    autoSubscribe: false;
    enableMacSafariVapid: true;
    defaultNotificationTitle: string;
    defaultNotificationIcon: string;
    serviceWorker: {
      url: string;
      scope: string;
      applicationServerPublicKey: string;
    };
  }) => Promise<void>;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  isSubscribed: () => Promise<boolean>;
  removeAllDeviceData: () => Promise<void>;
}

declare global {
  interface Window {
    WebEmarsysSdk?: EmarsysSdk;
  }
}

const SDK_URL =
  'https://client-version.cf.emarsys.net/web-emarsys-sdk-v4/4.8.0/web-emarsys-sdk.js';
const OWNER_KEY = 'livat-emarsys-application';
export const WEB_PUSH_CONSENT_KEY = 'livat-emarsys-push-opt-in-v1';
const LOCK = 'livat-emarsys';
let initialization: Promise<EmarsysSdk> | undefined;
let activeApplication: string | undefined;
let managedSdk: EmarsysSdk | undefined;
let withdrawal: Promise<void> | undefined;

export function withPushTimeout<Result>(
  operation: Promise<Result>,
): Promise<Result> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error('Web Push operation timed out')),
      15000,
    );
    operation.then(resolve, reject).finally(() => window.clearTimeout(timer));
  });
}

function identity(config: WebPushConfig): string {
  return `${config.applicationCode}:push-v1`;
}

function isLegacyOwner(config: WebPushConfig, owner: string): boolean {
  const [applicationCode, category] = owner.split(':');
  return (
    applicationCode === config.applicationCode &&
    /^C\d{4}$/.test(category ?? '')
  );
}

export function publishWebPushConsent(
  config: WebPushConfig,
  accepted: boolean,
): void {
  const owner = localStorage.getItem(OWNER_KEY);
  if (owner && owner !== identity(config) && !isLegacyOwner(config, owner))
    throw new Error('Conflicting Emarsys application on this origin');
  const value = `${identity(config)}:${accepted ? 'granted' : 'denied'}`;
  if (localStorage.getItem(WEB_PUSH_CONSENT_KEY) !== value)
    localStorage.setItem(WEB_PUSH_CONSENT_KEY, value);
}

export function hasSharedWebPushConsent(config: WebPushConfig): boolean {
  return (
    localStorage.getItem(WEB_PUSH_CONSENT_KEY) === `${identity(config)}:granted`
  );
}

function assertConsent(config: WebPushConfig): void {
  if (
    window.location.origin !== config.origin ||
    Notification.permission !== 'granted' ||
    !hasSharedWebPushConsent(config)
  ) {
    throw new Error('Web Push consent missing or origin not allowed');
  }
}

function readSdk(): EmarsysSdk | undefined {
  return window.WebEmarsysSdk;
}

export function supportsWebPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    'Notification' in window &&
    'PushManager' in window &&
    'serviceWorker' in navigator &&
    'locks' in navigator
  );
}

export function initializeWebPush(config: WebPushConfig): Promise<EmarsysSdk> {
  if (!supportsWebPush() || !hasSharedWebPushConsent(config)) {
    return Promise.reject(new Error('Web Push unavailable or consent missing'));
  }
  if (activeApplication && activeApplication !== identity(config)) {
    return Promise.reject(
      new Error('Web Push application conflicts on this origin'),
    );
  }
  if (withdrawal) return withdrawal.then(() => initializeWebPush(config));
  if (initialization) return initialization;
  activeApplication = identity(config);
  const pending = navigator.locks
    .request(LOCK, async () => {
      assertConsent(config);
      const owner = localStorage.getItem(OWNER_KEY);
      if (
        owner &&
        owner !== identity(config) &&
        !isLegacyOwner(config, owner)
      ) {
        throw new Error('Use one Emarsys application per origin');
      }
      if (window.WebEmarsysSdk && window.WebEmarsysSdk !== managedSdk) {
        throw new Error(
          'Remove the external Emarsys loader before enabling app integration',
        );
      }
      if (!managedSdk && document.readyState !== 'complete') {
        await withPushTimeout(
          new Promise<void>((resolve) =>
            window.addEventListener('load', () => resolve(), { once: true }),
          ),
        );
      }
      assertConsent(config);
      if (window.WebEmarsysSdk && window.WebEmarsysSdk !== managedSdk)
        throw new Error('External Emarsys loader detected');
      if (!managedSdk)
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          const timeout = window.setTimeout(() => {
            script.remove();
            reject(new Error('Emarsys script load timed out'));
          }, 15000);
          script.src = SDK_URL;
          script.async = true;
          script.onload = () => {
            window.clearTimeout(timeout);
            resolve();
          };
          script.onerror = () => {
            window.clearTimeout(timeout);
            script.remove();
            reject(new Error('Emarsys script failed to load'));
          };
          assertConsent(config);
          document.head.appendChild(script);
        });
      const sdk = readSdk();
      if (!sdk) throw new Error('Emarsys SDK unavailable');
      managedSdk = sdk;
      assertConsent(config);
      localStorage.setItem(OWNER_KEY, identity(config));
      const operation = sdk
        .init({
          applicationCode: config.applicationCode,
          autoSubscribe: false,
          enableMacSafariVapid: true,
          defaultNotificationTitle: config.title,
          defaultNotificationIcon: config.icon,
          serviceWorker: {
            url: 'emarsys-sw.js',
            scope: '/',
            applicationServerPublicKey: config.publicKey,
          },
        })
        .then(async () => {
          if (!hasSharedWebPushConsent(config)) {
            await withdrawLocalSubscription();
            throw new Error('Consent withdrawn during SDK initialization');
          }
        });
      await operation;
      return sdk;
    })
    .then((sdk) => sdk);
  initialization = pending;
  void pending.catch(() => {
    if (initialization === pending) initialization = undefined;
  });
  return pending;
}

export function ownsWebPush(config: WebPushConfig): boolean {
  const owner = localStorage.getItem(OWNER_KEY);
  return (
    owner === identity(config) ||
    (owner !== null && isLegacyOwner(config, owner))
  );
}

export async function subscribeToWebPush(config: WebPushConfig): Promise<void> {
  if (!supportsWebPush() || window.location.origin !== config.origin) {
    throw new Error('Web Push unavailable or origin not allowed');
  }
  publishWebPushConsent(config, true);
  const permission =
    Notification.permission === 'default'
      ? Notification.requestPermission()
      : Promise.resolve(Notification.permission);
  if ((await permission) !== 'granted') {
    publishWebPushConsent(config, false);
    await withdrawWebPushConsent(config);
    return;
  }
  assertConsent(config);
  await initializeWebPush(config);
  await changeWebPushSubscription(config, true);
}

export async function changeWebPushSubscription(
  config: WebPushConfig,
  subscribe: boolean,
): Promise<void> {
  assertConsent(config);
  const permission =
    subscribe && Notification.permission === 'default'
      ? Notification.requestPermission()
      : Promise.resolve(Notification.permission);
  if (subscribe && (await permission) !== 'granted') return;
  await navigator.locks.request(LOCK, async () => {
    assertConsent(config);
    if (!managedSdk) throw new Error('Web Push SDK not initialized');
    const operation = (
      subscribe ? managedSdk.subscribe() : managedSdk.unsubscribe()
    ).then(async () => {
      if (!hasSharedWebPushConsent(config)) await withdrawLocalSubscription();
    });
    await operation;
  });
}

export async function getWebPushSubscription(
  config: WebPushConfig,
): Promise<boolean> {
  return navigator.locks
    .request(LOCK, async () => {
      assertConsent(config);
      if (!managedSdk) throw new Error('Web Push SDK not initialized');
      return managedSdk.isSubscribed();
    })
    .then((subscribed) => subscribed);
}

async function withdrawLocalSubscription(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration('/');
  const worker =
    registration?.installing ?? registration?.waiting ?? registration?.active;
  if (
    registration &&
    worker &&
    ['/emarsys-sw.js', '/sw.js'].includes(new URL(worker.scriptURL).pathname)
  ) {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription && !(await subscription.unsubscribe())) {
      throw new Error('Web Push subscription removal failed');
    }
  }
}

export async function withdrawWebPushConsent(
  config: WebPushConfig,
): Promise<void> {
  if (!ownsWebPush(config)) return;
  if (hasSharedWebPushConsent(config)) return;
  if (withdrawal) return withdrawal;
  initialization = undefined;
  const consentAtWithdrawal = localStorage.getItem(WEB_PUSH_CONSENT_KEY);
  const cleanup = (async () => {
    const failures: unknown[] = [];
    const localRemoval = withPushTimeout(withdrawLocalSubscription()).catch(
      (error: unknown) => failures.push(error),
    );
    await navigator.locks.request(LOCK, async () => {
      await localRemoval;
      if (
        localStorage.getItem(WEB_PUSH_CONSENT_KEY) !== consentAtWithdrawal &&
        hasSharedWebPushConsent(config)
      )
        return;
      try {
        if (managedSdk) {
          await managedSdk
            .unsubscribe()
            .catch((error: unknown) => failures.push(error));
          await managedSdk
            .removeAllDeviceData()
            .catch((error: unknown) => failures.push(error));
        }
      } finally {
        await withdrawLocalSubscription().catch((error: unknown) =>
          failures.push(error),
        );
        await navigator.serviceWorker
          .register('/sw.js', { scope: '/' })
          .catch((error: unknown) => failures.push(error));
        initialization = undefined;
      }
    });
    if (failures.length)
      throw new Error('Web Push withdrawal incomplete; retry required');
  })();
  withdrawal = cleanup;
  try {
    await cleanup;
  } finally {
    if (withdrawal === cleanup) withdrawal = undefined;
  }
}

